/** A record page's loading and empty states (plan §1.6): skeleton panels while the record
 *  loads (never a spinner over content), and the standard empty state — what happened and
 *  where to go — for a record that does not exist or that the operator may not see. Each
 *  record kind words its own (`BookingNotFound`, `OrderNotFound`).
 *
 *  A catalog record's page loads in its own shape (`RecordPageSkeleton`, Zero-Re-Layout): the
 *  dock's column where the page will dock one, and its cards' skeletons in the loaded cards'
 *  rows (`RecordCardRows`) — so nothing moves sideways when the record arrives. */
import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { LEDGER_GAP_PX } from '../ledger/LedgerDock';
import { FIELD_GRID_SX } from '../panels/fieldGrid';
import { CARD_GAP_PX, RecordCardRows, type RecordCardColumns, type RecordCardRow } from '../panels/RecordCardRows';
import type { ViewSection } from '../views/types';

import { useElementWidth } from './recordPage';

function PanelSkeleton({ lines }: { lines: number }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Skeleton variant="text" width={140} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))', gap: 2 }}>
          {Array.from({ length: lines }, (_, i) => (
            <Box key={i}>
              <Skeleton variant="text" width="45%" />
              <Skeleton variant="rounded" height={32} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/** The spine and the stage panels while the record loads. `label` names what loads
 *  ("Loading the booking"); `nodes` is the spine's node count (a booking's five, an
 *  order's four). */
export function RecordSkeleton({ label, nodes = 5 }: { label: string; nodes?: number }) {
  return (
    <Box role="status" aria-label={label} data-record-skeleton>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${nodes}, minmax(0, 1fr))`, gap: 2, py: 1.5, mb: 2 }}>
        {Array.from({ length: nodes }, (_, i) => (
          <Box key={i}>
            <Skeleton variant="circular" width={14} height={14} />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
        <PanelSkeleton lines={8} />
        <PanelSkeleton lines={8} />
        <PanelSkeleton lines={6} />
        <PanelSkeleton lines={4} />
      </Box>
    </Box>
  );
}

/** One block of a card's skeleton: the Editable fields, or a read-only block (a hairline and
 *  a muted heading over shorter slots); `fields` slots share the field grid's rows, `wide`
 *  ones take a row each. */
export interface CardSkeletonBlock {
  readOnly: boolean;
  fields: number;
  wide: number;
}

/** A card's skeleton blocks from its section — the loaded card's blocks, slot for slot.
 *  `wide`: the record kind's full-row fields. */
export function cardSkeletonShape(section: ViewSection, wide: (key: string) => boolean): CardSkeletonBlock[] {
  return section.blocks.map((b) => {
    const full = b.fields.filter((f) => wide(f.key)).length;
    return { readOnly: b.kind !== 'editable', fields: b.fields.length - full, wide: full };
  });
}

/** A field slot's three parts (`FieldSlot`): the 24 px label row, the control part — the
 *  40 px an input takes, or the value's line for a read-only value — and the caption line. */
function SlotSkeleton({ readOnly, wide }: { readOnly: boolean; wide: boolean }) {
  return (
    <Box sx={wide ? { gridColumn: '1 / -1' } : undefined} data-slot-skeleton={readOnly ? 'read-only' : 'editable'}>
      <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
        <Skeleton width={wide ? '20%' : '45%'} />
      </Box>
      <Box sx={{ minHeight: readOnly ? 20 : 40, display: 'flex', alignItems: 'center' }}>
        <Skeleton width={wide ? '70%' : '60%'} sx={{ flex: 'none' }} />
      </Box>
      <Box sx={{ minHeight: readOnly ? 12 : 20 }} />
    </Box>
  );
}

/** A card while the record loads, on the loaded card's chrome (`SectionCard`): its title row
 *  (and `action`'s room at its end), then its blocks' slots on the field grid its column pins
 *  (`FIELD_GRID_SX`) — or `children` in their place (a card drawn by its own component). */
export function RecordCardSkeleton({
  blocks = [],
  action,
  children,
}: {
  blocks?: readonly CardSkeletonBlock[];
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Card variant="outlined" data-card-skeleton>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" component="div" sx={{ flex: 1 }}>
            <Skeleton width={140} />
          </Typography>
          {action}
        </Box>
        {children ?? (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {blocks.map((b, i) => (
              <Box
                key={i}
                // A read-only block's hairline and heading (`BlockRule`); the first block has no rule.
                sx={{ display: 'grid', gap: 0.75, ...(i > 0 ? { borderTop: 1, borderColor: 'divider', pt: 1.25 } : {}) }}
              >
                {b.readOnly && (
                  <Typography variant="caption" component="div">
                    <Skeleton width={180} />
                  </Typography>
                )}
                <Box sx={FIELD_GRID_SX}>
                  {Array.from({ length: b.fields }, (_, j) => (
                    <SlotSkeleton key={`f${j}`} readOnly={b.readOnly} wide={false} />
                  ))}
                  {Array.from({ length: b.wide }, (_, j) => (
                    <SlotSkeleton key={`w${j}`} readOnly={b.readOnly} wide />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export interface RecordPageSkeletonProps<K extends string> {
  /** The loaded page's width rule: from the measured content width (0 = unmeasured), whether
   *  the dock sits beside the cards, their edit-mode width and their column count. */
  layout: (width: number) => RecordCardColumns;
  /** The loaded cards' rows for a column count (`RecordCardRows`). */
  rows: (columns: 1 | 2) => readonly RecordCardRow<K>[];
  /** A card's skeleton by its key. */
  card: (key: K) => ReactNode;
  /** Above the rows, across the column (the activity's Where it's used). */
  lead?: ReactNode;
  /** The column the page docks beside the cards (`layout`'s `docked`): its width and its
   *  skeleton. */
  dock: { width: number; node: ReactNode };
}

/** A catalog record page's cards and dock while it loads (Zero-Re-Layout): measured like the
 *  loaded page's content (`LedgerDock`'s grid), it keeps the dock's column where the page will
 *  dock one, and lays the cards' skeletons out in the loaded cards' rows and field-grid
 *  columns. */
export function RecordPageSkeleton<K extends string>({ layout, rows, card, lead, dock }: RecordPageSkeletonProps<K>) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const { docked, editWidth, columns } = layout(width);
  return (
    <Box
      ref={ref}
      data-record-page-skeleton
      data-columns={columns}
      data-docked={docked || undefined}
      sx={{
        display: 'grid',
        gridTemplateColumns: docked ? `minmax(0, 1fr) ${dock.width}px` : 'minmax(0, 1fr)',
        columnGap: `${LEDGER_GAP_PX}px`,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: `${CARD_GAP_PX}px`, minWidth: 0 }}>
        {lead}
        <RecordCardRows rows={rows(columns)} editWidth={editWidth} card={card} />
      </Box>
      {docked && dock.node}
    </Box>
  );
}

export function RecordEmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ py: 8, px: 2, textAlign: 'center' }} data-record-empty>
      <Box sx={{ color: 'text.secondary', '& svg': { fontSize: 40 } }} aria-hidden>
        {icon}
      </Box>
      <Typography variant="h6" component="h2">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '60ch' }}>
        {children}
      </Typography>
      {action}
    </Stack>
  );
}
